import { ReportsRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { getReportManager } from '../reports/report-storage';
import { logger } from '../utils/logger';

/**
 * Handle unified reports management request
 * Supports operations: create, update, delete, getAll, getOne, query
 * Only accepts requests from 'admin' type
 *
 * Strategy: Try database collections first, fall back to file-based storage if failed
 */
export async function handleReportsRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      return null; // Collection not found, will fall back to file storage
    }
    return await handler(params);
  };

  try {
    const request = ReportsRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;
    const reportId = requestData?.reportId;
    const report = requestData?.report;
    const projectId = requestData?.projectId;
    const name = requestData?.name;
    const description = requestData?.description;
    const published = requestData?.published;
    const createdBy = requestData?.createdBy;
    const updatedBy = requestData?.updatedBy;
    const numericId = requestData?.id;
    // Query operation fields
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const sort = requestData?.sort;

    // Read-only operations allowed for all client types (runtime, admin, etc.)
    const readOnlyOperations = ['query', 'getAll', 'getOne'];

    // Write operations require admin
    if (!readOnlyOperations.includes(operation) && from.type !== 'admin') {
      sendResponse(id, {
        success: false,
        error: 'Unauthorized: Only admin can manage reports'
      }, sendMessage, from.id);
      logger.warn(`Unauthorized report write attempt from: ${from.type}`);
      return;
    }

    const reportManager = getReportManager();

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, reportId, report, projectId, name, description, published, createdBy, executeCollection, reportManager, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, numericId, reportId, report, name, description, published, updatedBy, executeCollection, reportManager, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, numericId, reportId, executeCollection, reportManager, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, executeCollection, reportManager, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, numericId, reportId, executeCollection, reportManager, sendMessage, from.id);
        break;

      case 'query':
        await handleQuery(id, filters, limit, sort, executeCollection, reportManager, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle reports request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create report operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleCreate(
  id: string,
  reportId: string | undefined,
  report: any,
  projectId: string | undefined,
  name: string | undefined,
  description: string | undefined,
  published: boolean | undefined,
  createdBy: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  reportManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!reportId || reportId.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Report ID is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  if (!projectId) {
    sendResponse(id, {
      success: false,
      error: 'Project ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('reports', 'create', {
      reportId,
      projectId,
      name: name || '',
      description,
      report,
      published,
      createdBy
    });

    if (result && result.success) {
      logger.info(`[DB] Report created successfully, ID: ${result.data?.id}`);
      sendResponse(id, {
        success: true,
        data: {
          id: result.data?.id,
          reportId: result.data?.reportId,
          report: result.data?.report || report,
          message: `Report created successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to create report, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage
  try {
    if (!report) {
      report = {};
    }

    const createdReport = reportManager.createReport(reportId, report);

    logger.info(`[FILE] Report '${reportId}' created successfully`);
    sendResponse(id, {
      success: true,
      data: {
        reportId,
        report: createdReport,
        message: `Report '${reportId}' created successfully (File)`
      }
    }, sendMessage, clientId);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create report'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update report operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleUpdate(
  id: string,
  numericId: number | undefined,
  reportId: string | undefined,
  report: any,
  name: string | undefined,
  description: string | undefined,
  published: boolean | undefined,
  updatedBy: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  reportManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'Report ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('reports', 'update', {
      id: numericId,
      name,
      description,
      report,
      published,
      updatedBy
    });

    if (result && result.success) {
      logger.info(`[DB] Report updated successfully, ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          reportId: result.data?.reportId,
          report: result.data?.report || report,
          message: `Report updated successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to update report, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage - use reportId if exists, otherwise stringify numericId
  const fileStorageId = reportId || String(numericId);
  try {
    const updatedReport = reportManager.updateReport(fileStorageId, report);

    if (!updatedReport) {
      sendResponse(id, {
        success: false,
        error: `Report '${fileStorageId}' not found`
      }, sendMessage, clientId);
      return;
    }

    logger.info(`[FILE] Report '${fileStorageId}' updated successfully`);
    sendResponse(id, {
      success: true,
      data: {
        id: numericId,
        reportId: fileStorageId,
        report: updatedReport,
        message: `Report '${fileStorageId}' updated successfully (File)`
      }
    }, sendMessage, clientId);
  } catch (error) {
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update report'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete report operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleDelete(
  id: string,
  numericId: number | undefined,
  reportId: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  reportManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'Report ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('reports', 'delete', { id: numericId });

    if (result && result.success) {
      logger.info(`[DB] Report deleted successfully, ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          message: `Report deleted successfully (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to delete report, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage - use reportId if exists, otherwise stringify numericId
  const fileStorageId = reportId || String(numericId);
  const deleted = reportManager.deleteReport(fileStorageId);

  if (!deleted) {
    sendResponse(id, {
      success: false,
      error: `Report '${fileStorageId}' not found`
    }, sendMessage, clientId);
    return;
  }

  logger.info(`[FILE] Report '${fileStorageId}' deleted successfully`);
  sendResponse(id, {
    success: true,
    data: {
      id: numericId,
      reportId: fileStorageId,
      message: `Report '${fileStorageId}' deleted successfully (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle get all reports operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleGetAll(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  reportManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Try database collection first
  try {
    const result = await executeCollection('reports', 'getAll', {});

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} reports`);
      sendResponse(id, {
        success: true,
        data: {
          reports: result.data,
          count: result.count,
          message: `Retrieved ${result.count} reports (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to get all reports, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage
  const reports = reportManager.getAllReports();

  logger.info(`[FILE] Retrieved ${reports.length} reports`);
  sendResponse(id, {
    success: true,
    data: {
      reports,
      count: reports.length,
      message: `Retrieved ${reports.length} reports (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle get one report operation
 * Tries database collection first, falls back to file storage if failed
 */
async function handleGetOne(
  id: string,
  numericId: number | undefined,
  reportId: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  reportManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Validate input
  if (!numericId) {
    sendResponse(id, {
      success: false,
      error: 'Report ID is required'
    }, sendMessage, clientId);
    return;
  }

  // Try database collection first
  try {
    const result = await executeCollection('reports', 'getOne', { id: numericId });

    if (result && result.success) {
      logger.info(`[DB] Retrieved report ID: ${numericId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: numericId,
          reportId: result.data?.reportId,
          report: result.data?.report || result.data,
          message: `Retrieved report (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to get report, falling back to file storage: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage - use reportId if exists, otherwise stringify numericId
  const fileStorageId = reportId || String(numericId);
  const report = reportManager.getReport(fileStorageId);

  if (!report) {
    sendResponse(id, {
      success: false,
      error: `Report '${fileStorageId}' not found`
    }, sendMessage, clientId);
    return;
  }

  logger.info(`[FILE] Retrieved report: ${fileStorageId}`);
  sendResponse(id, {
    success: true,
    data: {
      id: numericId,
      reportId: fileStorageId,
      report,
      message: `Retrieved report '${fileStorageId}' (File)`
    }
  }, sendMessage, clientId);
}

/**
 * Handle query reports operation with filters
 * Database only - no file fallback for query operations
 */
async function handleQuery(
  id: string,
  filters: any,
  limit: number | undefined,
  sort: string | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  reportManager: any,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  // Try database collection
  try {
    const result = await executeCollection('reports', 'query', {
      filters: filters || {},
      limit,
      sort
    });

    if (result && result.success) {
      logger.info(`[DB] Query returned ${result.count} reports`);
      sendResponse(id, {
        success: true,
        data: {
          reports: result.data,
          count: result.count,
          message: `Query returned ${result.count} reports (DB)`
        }
      }, sendMessage, clientId);
      return;
    }
  } catch (dbError) {
    logger.warn(`[DB] Failed to query reports: ${dbError instanceof Error ? dbError.message : 'Unknown error'}`);
  }

  // Fallback to file-based storage (get all and return)
  const reports = reportManager.getAllReports();

  logger.info(`[FILE] Retrieved ${reports.length} reports (all - no query filter)`);
  sendResponse(id, {
    success: true,
    data: {
      reports,
      count: reports.length,
      message: `Retrieved ${reports.length} reports (File - no query filter)`
    }
  }, sendMessage, clientId);
}

/**
 * Send reports response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'REPORTS_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'admin',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
